/**
 * Mapeo de estados internos a estados visibles simplificados
 * 
 * Esta configuración permite mantener la funcionalidad interna completa
 * mientras se muestra estados simplificados al usuario.
 * 
 * Estados visibles:
 * - Abierto: incluye abierto, nuevo, esperando_cliente, en_espera
 * - En Progreso: incluye en_progreso
 * - Completado: incluye pendiente_validacion
 * - Prueba: incluye pendiente_validacion_nube
 * - Cerrado: incluye resuelto y cerrado
 */

// Estados visibles (lo que ve el usuario)
export const ESTADOS_VISIBLES = {
  ABIERTO: 'abierto',
  EN_PROGRESO: 'en_progreso',
  COMPLETADO: 'completado',
  PRUEBA: 'prueba',
  CERRADO: 'cerrado'
};

// Mapeo de estados internos a estados visibles
export const MAPEO_ESTADOS = {
  // Estados que se muestran como "Abierto"
  abierto: ESTADOS_VISIBLES.ABIERTO,
  nuevo: ESTADOS_VISIBLES.ABIERTO,
  esperando_cliente: ESTADOS_VISIBLES.ABIERTO,
  en_espera: ESTADOS_VISIBLES.ABIERTO,
  
  // Estados que se muestran como "En Progreso"
  en_progreso: ESTADOS_VISIBLES.EN_PROGRESO,
  
  // Estados que se muestran como "Completado"
  pendiente_validacion: ESTADOS_VISIBLES.COMPLETADO,
  
  // Estados que se muestran como "Prueba"
  pendiente_validacion_nube: ESTADOS_VISIBLES.PRUEBA,
  
  // Estados que se muestran como "Cerrado"
  resuelto: ESTADOS_VISIBLES.CERRADO,
  cerrado: ESTADOS_VISIBLES.CERRADO
};

// Mapeo inverso: cuando el usuario selecciona un estado visible,
// ¿a qué estado interno debería cambiar?
export const MAPEO_SELECCION_A_INTERNO = {
  [ESTADOS_VISIBLES.ABIERTO]: {
    // Si el ticket ya está en un estado "Abierto", mantener el estado actual
    default: 'abierto', // Estado por defecto al seleccionar "Abierto" desde otro estado
    preserveCurrent: true // Si está en un estado de "Abierto", mantenerlo
  },
  [ESTADOS_VISIBLES.EN_PROGRESO]: 'en_progreso',
  [ESTADOS_VISIBLES.COMPLETADO]: 'pendiente_validacion',
  [ESTADOS_VISIBLES.PRUEBA]: 'pendiente_validacion_nube',
  [ESTADOS_VISIBLES.CERRADO]: {
    // Si el ticket ya está en "resuelto", cambiar a "cerrado" (cerrar definitivamente)
    // Si el ticket está en cualquier otro estado, cambiar a "resuelto" (permite reapertura)
    default: 'resuelto', // Por defecto, cambiar a resuelto (permite reapertura)
    fromResuelto: 'cerrado' // Si ya está resuelto, cerrar definitivamente
  }
};

// Labels para mostrar al usuario
export const LABELS_ESTADOS_VISIBLES = {
  [ESTADOS_VISIBLES.ABIERTO]: 'Abierto',
  [ESTADOS_VISIBLES.EN_PROGRESO]: 'En Progreso',
  [ESTADOS_VISIBLES.COMPLETADO]: 'Completado',
  [ESTADOS_VISIBLES.PRUEBA]: 'Prueba',
  [ESTADOS_VISIBLES.CERRADO]: 'Cerrado'
};

// Configuración de colores para los estados visibles
export const COLORES_ESTADOS_VISIBLES = {
  [ESTADOS_VISIBLES.ABIERTO]: {
    label: 'Abierto',
    color: '#1976d2', // Azul (similar a "en_progreso")
    bgcolor: '#e3f2fd'
  },
  [ESTADOS_VISIBLES.EN_PROGRESO]: {
    label: 'En Progreso',
    color: '#0288d1',
    bgcolor: '#e1f5fe'
  },
  [ESTADOS_VISIBLES.COMPLETADO]: {
    label: 'Completado',
    color: '#2e7d32', // Verde (similar a "resuelto")
    bgcolor: '#e8f5e9'
  },
  [ESTADOS_VISIBLES.PRUEBA]: {
    label: 'Prueba',
    color: '#9c27b0', // Morado (para diferenciarlo)
    bgcolor: '#f3e5f5'
  },
  [ESTADOS_VISIBLES.CERRADO]: {
    label: 'Cerrado',
    color: '#616161', // Gris (igual que "cerrado")
    bgcolor: '#eeeeee'
  }
};

/**
 * Función helper: obtener estado visible desde estado interno
 * 
 * @param {string} estadoInterno - Estado interno del ticket (nuevo, en_progreso, etc.)
 * @returns {string} - Estado visible (abierto, en_progreso, completado, prueba, cerrado)
 */
export const getEstadoVisible = (estadoInterno) => {
  if (!estadoInterno) return ESTADOS_VISIBLES.ABIERTO;
  return MAPEO_ESTADOS[estadoInterno] || ESTADOS_VISIBLES.ABIERTO;
};

/**
 * Función helper: obtener estado interno desde estado visible seleccionado
 * 
 * @param {string} estadoVisible - Estado visible seleccionado (abierto, en_progreso, completado, prueba, cerrado)
 * @param {string} estadoActual - Estado interno actual del ticket
 * @returns {string} - Estado interno a usar
 */
export const getEstadoInternoDesdeVisible = (estadoVisible, estadoActual) => {
  const config = MAPEO_SELECCION_A_INTERNO[estadoVisible];
  
  if (!config) {
    return estadoActual; // Mantener estado actual si no hay mapeo
  }
  
  // Si tiene preserveCurrent, verificar si el estado actual ya está en el grupo
  if (typeof config === 'object' && config.preserveCurrent) {
    const estadoVisibleActual = getEstadoVisible(estadoActual);
    if (estadoVisibleActual === estadoVisible) {
      return estadoActual; // Mantener estado interno actual
    }
  }
  
  // Lógica especial para CERRADO: si ya está en resuelto, cerrar definitivamente
  if (typeof config === 'object' && config.fromResuelto && estadoActual === 'resuelto') {
    return config.fromResuelto; // Cerrar definitivamente si ya está resuelto
  }
  
  // Si tiene default, usar ese
  if (typeof config === 'object' && config.default) {
    return config.default;
  }
  
  // Si es string directo, usarlo
  if (typeof config === 'string') {
    return config;
  }
  
  return estadoActual;
};

/**
 * Función helper: obtener todos los estados internos que corresponden a un estado visible
 * Útil para filtros
 * 
 * @param {string} estadoVisible - Estado visible (abierto, en_progreso, completado, prueba, cerrado)
 * @returns {string[]} - Array de estados internos
 */
export const getEstadosInternosDesdeVisible = (estadoVisible) => {
  return Object.keys(MAPEO_ESTADOS).filter(
    estadoInterno => MAPEO_ESTADOS[estadoInterno] === estadoVisible
  );
};

/**
 * Función helper: obtener el label de un estado visible
 * 
 * @param {string} estadoVisible - Estado visible
 * @returns {string} - Label del estado
 */
export const getLabelEstadoVisible = (estadoVisible) => {
  return LABELS_ESTADOS_VISIBLES[estadoVisible] || estadoVisible;
};
